const LOCAL_DATA_URL = "/data/stations.json";
const MINISTRY_DATA_URL =
  "https://sedeaplicaciones.minetur.gob.es/ServiciosRESTCarburantes/PreciosCarburantes/EstacionesTerrestres/";

export const FUELS = {
  gasolina95: "Gasolina 95",
  gasolina98: "Gasolina 98",
  gasoleoA: "Diésel A",
  gasoleoB: "Diésel B",
};

function isStation(station) {
  return (
    station &&
    typeof station.id === "string" &&
    Number.isFinite(station.lat) &&
    Number.isFinite(station.lng) &&
    station.prices &&
    typeof station.prices === "object"
  );
}

function clean(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function number(value) {
  const parsed = Number.parseFloat(clean(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function firstPrice(row, fields) {
  for (const field of fields) {
    const value = number(row[field]);
    if (value != null) return value;
  }
  return null;
}

export function normalizeMinistryPayload(payload) {
  if (!payload || !Array.isArray(payload.ListaEESSPrecio)) return payload;

  const stations = payload.ListaEESSPrecio.map((row) => {
    const lat = number(row.Latitud);
    const lng = number(row["Longitud (WGS84)"]);
    if (lat == null || lng == null) return null;

    const province = clean(row.Provincia);
    const municipality = clean(row.Municipio);
    const locality = clean(row.Localidad);
    const rawAddress = clean(row["Dirección"]).replace(/\s*,\s*,+/g, ",");
    const brand = clean(row["Rótulo"]);
    const address = [...new Set([rawAddress, locality, municipality].filter(Boolean))].join(", ");

    return {
      id: clean(row.IDEESS) || `${province}|${municipality}|${rawAddress}|${lat}|${lng}`,
      name: brand || locality || municipality || rawAddress || "Gasolinera",
      brand: brand || null,
      address,
      province,
      municipality,
      locality,
      postalCode: clean(row["C.P."]),
      lat,
      lng,
      updatedAt: clean(payload.Fecha),
      prices: {
        gasolina95: firstPrice(row, ["Precio Gasolina 95 E5", "Precio Gasolina 95 E10", "Precio Gasolina 95 E5 Premium"]),
        gasolina98: firstPrice(row, ["Precio Gasolina 98 E5", "Precio Gasolina 98 E10"]),
        gasoleoA: firstPrice(row, ["Precio Gasoleo A", "Precio Gasoleo Premium"]),
        gasoleoB: firstPrice(row, ["Precio Gasoleo B"]),
      },
    };
  }).filter(Boolean);

  return {
    updatedAt: clean(payload.Fecha),
    source: "Precios oficiales del Ministerio para la Transición Ecológica y el Reto Demográfico",
    stations,
  };
}

function validatePayload(payload) {
  payload = normalizeMinistryPayload(payload);
  if (!payload || !Array.isArray(payload.stations)) throw new Error("Formato de datos no válido");
  const stations = payload.stations.filter(isStation);
  if (stations.length < 1000) throw new Error("La respuesta contiene muy pocas estaciones");
  return { ...payload, stations };
}

async function requestJson(url, signal) {
  const response = await fetch(url, {
    signal,
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Error ${response.status}`);
  return validatePayload(await response.json());
}

export async function loadStations(signal) {
  try {
    return { ...(await requestJson("/api/stations", signal)), isFallback: false };
  } catch (error) {
    if (error.name === "AbortError") throw error;
    try {
      return { ...(await requestJson(MINISTRY_DATA_URL, signal)), isFallback: false };
    } catch (ministryError) {
      if (ministryError.name === "AbortError") throw ministryError;
      if (import.meta.env.DEV || ["localhost", "127.0.0.1"].includes(window.location.hostname)) {
        return { ...(await requestJson(LOCAL_DATA_URL, signal)), isFallback: true };
      }
      throw ministryError;
    }
  }
}

export async function geocode(query, signal) {
  const localUrl = `/api/geocode?q=${encodeURIComponent(query)}`;
  try {
    const response = await fetch(localUrl, { signal, headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    const results = await response.json();
    if (!Array.isArray(results) || !results.length) throw new Error("Sin resultados");
    return results;
  } catch (error) {
    if (error.name === "AbortError") throw error;
    const fallback = new URL("https://nominatim.openstreetmap.org/search");
    fallback.searchParams.set("format", "jsonv2");
    fallback.searchParams.set("limit", "5");
    fallback.searchParams.set("countrycodes", "es");
    fallback.searchParams.set("accept-language", "es");
    fallback.searchParams.set("q", query);
    const response = await fetch(fallback, { signal, headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("No se pudo buscar esa ubicación");
    const results = await response.json();
    if (!Array.isArray(results) || !results.length) throw new Error("No se encontró esa ubicación");
    return results.map((item) => ({
      lat: Number(item.lat),
      lng: Number(item.lon),
      label: item.display_name,
    }));
  }
}
